import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CommitteeGroup } from '@conference/shared';
import { eventContentService } from '../services/eventContentService';
import { Button, ConfirmDialog, Input, Modal, Textarea } from '../components/ui';

type DraftMember = Pick<CommitteeGroup['members'][number], 'id' | 'name' | 'sort_order'>;
type DraftGroup = Pick<CommitteeGroup, 'id' | 'title' | 'sort_order'> & { members: DraftMember[] };
type GroupDraft = { editingId: string | null; title: string; memberNames: string };

function moveItem<T>(items: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function committeePayload(groups: DraftGroup[]) {
  return groups.map((group, groupIndex) => ({
    title: group.title.trim(),
    sort_order: groupIndex,
    members: group.members
      .filter((member) => member.name.trim())
      .map((member, memberIndex) => ({ name: member.name.trim(), sort_order: memberIndex })),
  }));
}

function parseMemberNames(value: string): string[] {
  return value.split(/[\n,;]+/).map((name) => name.trim()).filter(Boolean);
}

export default function EventCommitteePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [groups, setGroups] = useState<DraftGroup[]>([]);
  const [draft, setDraft] = useState<GroupDraft | null>(null);
  const [pendingGroups, setPendingGroups] = useState<DraftGroup[] | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<DraftGroup | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!eventId) return;
    eventContentService.getCommittee(eventId)
      .then((committee) => setGroups(committee.groups))
      .catch((reason) => setError(reason instanceof Error ? reason.message : 'Could not load committee.'));
  }, [eventId]);

  const persist = async (next: DraftGroup[]) => {
    if (!eventId) return false;
    setSaving(true);
    setError('');
    try {
      await eventContentService.saveCommittee(eventId, committeePayload(next));
      setGroups(next.map((group, groupIndex) => ({
        ...group,
        sort_order: groupIndex,
        members: group.members.filter((member) => member.name.trim()).map((member, memberIndex) => ({ ...member, name: member.name.trim(), sort_order: memberIndex })),
      })));
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save committee.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => setDraft({ editingId: null, title: '', memberNames: '' });
  const openEdit = (group: DraftGroup) => setDraft({
    editingId: group.id,
    title: group.title,
    memberNames: group.members.map((member) => member.name).join('\n'),
  });

  const nextGroupsFromDraft = () => {
    if (!draft || !draft.title.trim()) {
      setError('Group name is required.');
      return null;
    }
    const groupId = draft.editingId ?? `new-group-${Date.now()}`;
    const group: DraftGroup = {
      id: groupId,
      title: draft.title.trim(),
      sort_order: groups.length,
      members: parseMemberNames(draft.memberNames).map((name, index) => ({
        id: `${groupId}-member-${index}`,
        name,
        sort_order: index,
      })),
    };
    return draft.editingId
      ? groups.map((current) => current.id === draft.editingId ? group : current)
      : [...groups, group];
  };

  const requestGroupSave = async () => {
    const next = nextGroupsFromDraft();
    if (!next || !draft) return;
    if (draft.editingId) {
      setPendingGroups(next);
      return;
    }
    if (await persist(next)) setDraft(null);
  };

  const saveEditedGroup = async () => {
    if (!pendingGroups) return;
    if (await persist(pendingGroups)) {
      setPendingGroups(null);
      setDraft(null);
    }
  };

  const deleteGroup = async () => {
    if (!groupToDelete) return;
    if (await persist(groups.filter((group) => group.id !== groupToDelete.id))) setGroupToDelete(null);
  };

  const moveGroup = async (index: number, direction: -1 | 1) => {
    await persist(moveItem(groups, index, direction));
  };

  return <div className="max-w-4xl p-8">
    <div className="mb-6 flex justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-white">Organizing Committee</h1>
        <p className="mt-1 text-sm text-gray-400">Create groups, add members, and set their attendee-facing order.</p>
      </div>
      <Link to="/events" className="text-sm text-sky-400">Back to events</Link>
    </div>

    {error && <p role="alert" className="mb-4 rounded bg-red-500/10 p-3 text-sm text-red-300">{error}</p>}
    <Button className="mb-4" onClick={openCreate}>Add group</Button>

    <div className="space-y-3">
      {groups.map((group, groupIndex) => <section key={group.id} className="rounded-xl border border-gray-800 bg-gray-900 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-white">{group.title}</h2>
            {group.members.length ? (
              <ul className="mt-3 space-y-1.5">
                {group.members.map((member) => <li key={member.id} className="text-sm text-gray-300">{member.name}</li>)}
              </ul>
            ) : <p className="mt-2 text-sm text-gray-500">No members added.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" aria-label={`Move ${group.title} up`} disabled={saving || groupIndex === 0} onClick={() => void moveGroup(groupIndex, -1)}>Up</Button>
            <Button type="button" variant="secondary" size="sm" aria-label={`Move ${group.title} down`} disabled={saving || groupIndex === groups.length - 1} onClick={() => void moveGroup(groupIndex, 1)}>Down</Button>
            <Button type="button" variant="secondary" size="sm" aria-label={`Edit ${group.title}`} onClick={() => openEdit(group)}>Edit</Button>
            <Button type="button" variant="danger" size="sm" aria-label={`Delete ${group.title} group`} onClick={() => setGroupToDelete(group)}>Delete</Button>
          </div>
        </div>
      </section>)}
    </div>

    <Modal open={draft !== null} onClose={() => !saving && setDraft(null)} title={draft?.editingId ? 'Edit committee group' : 'Add committee group'} description="Enter the group name and members in display order." size="lg">
      {draft && <form onSubmit={(event) => { event.preventDefault(); void requestGroupSave(); }} className="space-y-4">
        <Input label="Group name" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required />
        <Textarea label="Members" rows={7} value={draft.memberNames} onChange={(event) => setDraft({ ...draft, memberNames: event.target.value })}
          hint="Enter one name per line, or separate names with commas or semicolons." />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
          <Button type="submit" loading={saving}>{draft.editingId ? 'Save group' : 'Add group'}</Button>
        </div>
      </form>}
    </Modal>

    <ConfirmDialog open={pendingGroups !== null} onClose={() => setPendingGroups(null)} onConfirm={() => void saveEditedGroup()} loading={saving}
      intent="primary" title="Save changes?" description="Apply the current committee group changes?" confirmLabel="Save changes" />
    <ConfirmDialog open={groupToDelete !== null} onClose={() => setGroupToDelete(null)} onConfirm={() => void deleteGroup()} loading={saving}
      title="Delete committee group?" description={`Delete ${groupToDelete?.title ?? 'this group'} and all of its members?`} confirmLabel="Delete group" />
  </div>;
}
